import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getRouteData } from '@common';
import { GAEvent } from '../models/ga-event.enum';
import { GAPageView } from '../models/ga-page-view.interface';
import { GARouteData } from '../models/ga-route-data.interface';
import { GATag } from '../models/ga-tag.type';
import { GA_CONFIG } from '../tokens/ga-config.token';
import { GA_GLOBAL_DATA } from '../tokens/ga-global-data.token';
import { GOOGLE_TAG_MANAGER } from '../tokens/google-tag-manager.token';
import { extractPageNameFromUrl } from '../utils/router/router.utils';

@Injectable({
  providedIn: 'root',
})
export class GAService {
  private readonly gaConfig = inject(GA_CONFIG);
  private readonly gaGlobalData = inject<GATag | null>(GA_GLOBAL_DATA, {
    optional: true,
  });
  private readonly gtm = inject(GOOGLE_TAG_MANAGER);
  private readonly route = inject(ActivatedRoute);

  private dynamicData: GATag = {};

  constructor() {
    if (this.gaGlobalData) {
      this.dynamicData = { ...this.gaGlobalData };
    }
  }

  tag(...tags: GATag[]): void {
    tags.forEach((tag: GATag) => {
      const gaTag: GATag = {
        brand: this.gaConfig.clientBrandCode,
        category: this.gaConfig.category,
        domainId: this.gaConfig.domainId,
        pagename: extractPageNameFromUrl(),
        version: this.gaConfig.version,
        ...this.globalData,
        ...this.routeData,
        ...tag,
      };
      this.gtm.push(gaTag);

      if (this.gaConfig.debug) {
        console.log(
          '%c🕵️ GTM Event',
          'font-weight:bold; font-size:18px; margin-bottom:8px;',
        );
        console.table(gaTag);
      }
    });
  }

  pageView(tag: GATag): void {
    const pageView: GAPageView = {
      ...this.globalData,
      ...this.routeData,
      brand: this.gaConfig.clientBrandCode,
      category: this.gaConfig.category,
      country: this.gaConfig.countryCode,
      domainId: this.gaConfig.domainId,
      event: GAEvent.PageView,
      type: this.gaConfig.type,
      version: this.gaConfig.version,
      ...tag,
    };
    this.gtm.push(pageView);

    if (this.gaConfig.debug) {
      console.log(
        '%c🕵️ GTM PageView',
        'font-weight:bold; font-size:18px; margin-bottom:8px;',
      );
      console.table(pageView);
    }
  }

  addToGlobalData(data: GATag): void {
    this.dynamicData = { ...this.dynamicData, ...data };
  }

  removeFromGlobalData<K extends keyof GATag>(...keys: K[]): void {
    keys.forEach((key: K) => delete this.dynamicData[key]);
  }

  private get routeData(): GARouteData {
    const routeData = getRouteData(this.route.snapshot);
    return routeData['gaData'];
  }

  private get globalData(): GATag {
    return { ...this.dynamicData };
  }
}
